import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import api from '@/lib/api';
import type { TodoItemResponse } from '@/lib/api';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { useCompletedTimeline, useTodoSections, useTodoList, useTodoListSubtasks } from '../hooks';
import { usePrincipalDisplayNames } from '../hooks/usePrincipalDisplayNames';
import {
  CompletedTimeline,
  AssigneeFilterBar,
  type AssigneeFilterValue,
  type CompletedTimelineItem,
} from '../components';

export function CompletedTimelinePage() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    selectedHolderId,
    selectedOption,
    principalId,
    isLoading: switcherLoading,
  } = useHolderSwitcher();

  const { list } = useTodoList(selectedHolderId, listId!);
  const { sections } = useTodoSections(selectedHolderId, listId!);
  // Sub-tasks (all statuses) grouped by parent, so a completed task with
  // sub-tasks can expand to show them inline.
  const { subtasksByParent } = useTodoListSubtasks(selectedHolderId, listId!);
  const {
    items: rawItems,
    loading,
    error,
  } = useCompletedTimeline({
    holderId: selectedHolderId,
    listId: listId!,
  });
  // This page only ever uses the authenticated surface (holderId + listId), so
  // the items always carry the full TodoItemResponse shape (incl. assignee).
  const sharedItems = rawItems as TodoItemResponse[];

  // The viewer's own completed/cancelled PRIVATE items — principal-scoped, so
  // this only ever returns yours. Folded into the timeline under "My tasks".
  const { data: personalData, isLoading: personalLoading } = useQuery({
    queryKey: queryKeys.todoLists.completedPersonal(selectedHolderId ?? '', listId!),
    queryFn: () => api.listCompletedPersonalTodoItems(selectedHolderId as string, listId!),
    enabled: !!selectedHolderId && !!listId,
  });

  const { resolve: resolveCompletedBy, members: assignableMembers } = usePrincipalDisplayNames();

  // Assignee filter — mirrors the active list. Only meaningful in a shared
  // (Org) workspace; every member gets a chip per teammate, plus "My tasks".
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilterValue>('all');
  const isOrg = selectedOption?.holderType === 'org';

  // Adapt each private item into a timeline row (marked isPrivate → renders a
  // "Private" badge, no KEY-N id) and tag it as the viewer's so it lands under
  // "My tasks". Its itemId is the personalItemId, which the uncomplete handler
  // routes back to the personal-item update.
  const adaptedPersonal = useMemo<CompletedTimelineItem[]>(
    () =>
      (personalData?.items ?? []).map(
        (p) =>
          ({
            itemId: p.personalItemId,
            listId: p.listId,
            sectionId: null,
            parentItemId: null,
            title: p.title,
            description: p.description,
            status: p.status,
            seq: 0,
            completedAt: p.completedAt,
            completedByPrincipalId: null,
            assignedToPrincipalId: principalId ?? null,
            projectId: p.projectId,
            checklistTotal: p.checklistTotal ?? 0,
            checklistDone: p.checklistDone ?? 0,
            subtaskTotal: 0,
            isPrivate: true,
          }) as unknown as CompletedTimelineItem,
      ),
    [personalData, principalId],
  );

  // Shared + private, newest completion first; cancelled (no completedAt) sink
  // to the bottom (grouped under "Cancelled" by the timeline).
  const timelineItems = useMemo<CompletedTimelineItem[]>(
    () =>
      [...sharedItems, ...adaptedPersonal].sort((a, b) => {
        const at = a.completedAt ? Date.parse(a.completedAt) : -Infinity;
        const bt = b.completedAt ? Date.parse(b.completedAt) : -Infinity;
        return bt - at;
      }),
    [sharedItems, adaptedPersonal],
  );

  const filteredItems = useMemo(() => {
    return timelineItems.filter((item) => {
      // Private items are yours alone — show them only under "My tasks" (never
      // under Everyone or another member). A personal list has no assignee axis
      // (no filter bar), so they always show there.
      if (item.isPrivate) return !isOrg || assigneeFilter === 'mine';
      if (assigneeFilter === 'all') return true;
      if (assigneeFilter === 'mine')
        return !!principalId && item.assignedToPrincipalId === principalId;
      if (assigneeFilter === 'unassigned') return !item.assignedToPrincipalId;
      return item.assignedToPrincipalId === assigneeFilter;
    });
  }, [timelineItems, assigneeFilter, principalId, isOrg]);

  const handleUncomplete = async (item: CompletedTimelineItem) => {
    if (!selectedHolderId) return;
    if (item.isPrivate) {
      // itemId is the personalItemId — route back to the personal-item update.
      await api.updateTodoPersonalItem(selectedHolderId, item.itemId, { status: 'pending' });
      queryClient.invalidateQueries({
        queryKey: queryKeys.todoLists.completedPersonal(selectedHolderId, listId!),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.todoLists.personalBoard(selectedHolderId, listId!),
      });
      return;
    }
    const sectionExists = item.sectionId && sections.some((s) => s.sectionId === item.sectionId);
    await api.updateTodoItem(selectedHolderId, item.itemId, {
      status: 'pending',
      ...(item.sectionId && !sectionExists ? { sectionId: null } : {}),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.todoLists.detail(selectedHolderId, listId!),
    });
  };

  if (switcherLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (!selectedHolderId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <p className="text-sm text-muted-foreground">Select a workspace to view completed items.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/a/arche_todo_lists/${listId}`)}
          aria-label="Back to list"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">Completed &amp; Cancelled</h1>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading || personalLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      ) : timelineItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No completed or cancelled items yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {isOrg && (
            <AssigneeFilterBar
              value={assigneeFilter}
              onChange={setAssigneeFilter}
              members={assignableMembers}
              myPrincipalId={principalId}
            />
          )}
          {filteredItems.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <p>No completed or cancelled tasks match this filter.</p>
            </div>
          ) : (
            <CompletedTimeline
              items={filteredItems}
              sections={sections}
              loading={loading}
              showCompletedBy
              resolveCompletedBy={resolveCompletedBy}
              onUncomplete={handleUncomplete}
              itemKeyPrefix={list?.itemKey}
              subtasksByParent={subtasksByParent}
              holderId={selectedHolderId}
              listId={listId!}
            />
          )}
        </div>
      )}
    </div>
  );
}
